































































































































































import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { AxiosError } from 'axios';

import { ALForm } from '@/common/component.types';

import {
  Employee,
  EmployeeFieldError,
  EmployeeTimeOffFieldError,
  EmployeeType,
  EmployeeWorkSchedule,
  EmployeeWorkScheduleFieldError,
  IEmployeeData,
  IEmployeeTimeOffData,
  IEmployeeTimeOffEntitlementData,
  IEmployeeWorkScheduleData
} from '@/models';

import EmployeeTimeOffDetailFormCard from '@/components/employee-time-off/EmployeeTimeOffDetailFormCard.vue';
import EmployeeScheduleDetailFormCard from '@/components/employee/EmployeeScheduleDetailFormCard.vue';
import EmployeeDetailFormCard from '@/components/employee/EmployeeDetailFormCard.vue';
import { ErrorManager } from '@/models/error';
import { cloneDeep } from 'lodash';

import ALDialog from '../core/Dialog/ALDialog.vue';
import ErrorAlert from '../common/ErrorAlert.vue';

type ALFormRefs =
  | 'employeeFormDetail'
  | 'employeeFormTimeoff'
  | 'employeeFormSchedule';

interface Options extends InstanceType<typeof Vue> {
  $refs: {
    employeeFormDetail: ALForm;
    employeeFormTimeoff: ALForm;
    employeeFormSchedule: ALForm;
    employeeDetailFormCard: InstanceType<typeof EmployeeDetailFormCard>;
    employeeTimeOffDetailFormCard: InstanceType<
      typeof EmployeeTimeOffDetailFormCard
    >;
    employeeScheduleDetailFormCard: InstanceType<
      typeof EmployeeScheduleDetailFormCard
    >;
  };
}

enum EmployeeProfileTabs {
  Detail = 'employee-detail',
  Timeoff = 'employee-timeoff',
  Schedule = 'employee-schedule'
}

export default Vue.extend<Options>({ functional: false }).extend({
  name: 'EmployeeDetailProfileFormDialog',

  components: {
    EmployeeTimeOffDetailFormCard,
    EmployeeScheduleDetailFormCard,
    EmployeeDetailFormCard,
    ALDialog,
    ErrorAlert
  },

  data() {
    return {
      dialog: false,
      loading: false,
      tab: EmployeeProfileTabs.Detail,
      errorTab: null as EmployeeProfileTabs | null,

      employee: {
        first_name: '',
        last_name: '',
        type: EmployeeType.Salary
      } as IEmployeeData,
      employeeTimeOff: {} as IEmployeeTimeOffData,
      employeeTimeOffEntitlements: {} as Record<
        number,
        IEmployeeTimeOffEntitlementData[]
      >,
      entitlementYearsModified: [] as number[],
      employeeWorkSchedule: {
        scheduled_days: [],
        workday_hours: 0
      } as IEmployeeWorkScheduleData,

      errorMessage: '',
      errorMessageDetail: '',

      employeeWorkScheduleFieldError: new EmployeeWorkScheduleFieldError(),
      employeeTimeOffFieldError: new EmployeeTimeOffFieldError(),
      employeeDetailFieldError: new EmployeeFieldError(),

      EmployeeProfileTabs,

      employeeDetailFormValid: false,
      employeeTimeoffFormValid: false,
      employeeScheduleFormValid: false
    };
  },

  computed: {
    ...mapGetters({
      getEmployeeById: 'employee/getEmployeeById',
      getEmployeeTimeOff: 'employeeTimeOff/getEmployeeTimeOff',
      getEmployeeTimeOffEntitlements:
        'employeeTimeOff/getEmployeeTimeOffEntitlements',
      getEmployeeWorkSchedule: 'employee/getEmployeeWorkSchedule'
    }),

    formTitle(): string {
      let title = 'Employee Profile';
      if (this.employee && this.employee.first_name) {
        title = `${this.employee.first_name}`;
        // apostrophize name of employee accordingly
        if (title.endsWith('s')) {
          title += "'";
        } else {
          title += "'s";
        }
        title += ' Employee Profile';
      }
      return title;
    },

    employeeProfileFormValidity(): boolean {
      return (
        this.employeeDetailFormValid &&
        this.employeeTimeoffFormValid &&
        this.employeeScheduleFormValid
      );
    }
  },

  methods: {
    ...mapActions({
      fetchEmployeeTimeOff: 'employeeTimeOff/fetchEmployeeTimeOff',
      fetchEmployeeTimeOffEntitlements:
        'employeeTimeOff/fetchEmployeeTimeOffEntitlements',
      fetchEmployeeWorkSchedule: 'employee/fetchEmployeeWorkSchedule'
    }),

    resetFormValidations(): void {
      if (this.$refs.employeeFormDetail) {
        this.$refs.employeeFormDetail.resetValidation();
      }
      if (this.$refs.employeeFormTimeoff) {
        this.$refs.employeeFormTimeoff.resetValidation();
      }
      if (this.$refs.employeeFormSchedule) {
        this.$refs.employeeFormSchedule.resetValidation();
      }
    },

    validateForm(refKey: ALFormRefs): boolean {
      let valid = false;
      if (this.$refs[refKey]) {
        valid = this.$refs[refKey].validate();
      }

      return valid;
    },

    async open(employee: IEmployeeData) {
      this.employee = new Employee(employee);

      // fetch current employee timeoff for employee
      await this.fetchCurrentEmployeeTimeOff(employee.id!);

      this.employeeTimeOff = this.getEmployeeTimeOff ?? {};

      //reset entitlement data
      this.entitlementYearsModified = [];
      this.employeeTimeOffEntitlements = {};

      await this.searchEmployeeTimeOffEntitlements(
        new Date().getFullYear(),
        employee.id!
      );

      await this.fetchCurrentEmployeeWorkSchedule(employee.id!);

      this.employeeWorkSchedule =
        cloneDeep(this.getEmployeeWorkSchedule) ??
        new EmployeeWorkSchedule({
          employee: employee.id,
          scheduled_days: [],
          workday_hours: 0
        });

      this.tab = EmployeeProfileTabs.Detail;

      this.dialog = true;
      this.resetFormValidations();
    },

    async fetchCurrentEmployeeTimeOff(employee: number): Promise<void> {
      await this.fetchEmployeeTimeOff(employee).catch(error => {
        this.errorMessage = ErrorManager.extractApiError(error);
      });
    },

    async fetchCurrentEmployeeWorkSchedule(employee: number): Promise<void> {
      await this.fetchEmployeeWorkSchedule(employee).catch(error => {
        this.errorMessage = ErrorManager.extractApiError(error);
      });
    },

    async searchEmployeeTimeOffEntitlements(year: number, employee: number) {
      return this.fetchEmployeeTimeOffEntitlements({
        year,
        employee: employee
      })
        .then(() => {
          //we use this to keep object reactivity. Vue cannot detect object property addition
          Vue.set(
            this.employeeTimeOffEntitlements,
            year,
            this.getEmployeeTimeOffEntitlements
          );
        })
        .catch(error => {
          this.errorMessage = ErrorManager.extractApiError(error);
        });
    },

    clearErrorTab() {
      this.errorTab = null;
    },

    setErrorTab(tab: EmployeeProfileTabs) {
      this.tab = tab;
      this.errorTab = tab;
    },

    close() {
      this.dialog = false;
    },

    clearAllErrors(): void {
      this.errorMessage = '';
      this.errorMessageDetail = '';

      this.employeeDetailFieldError.clearErrors();
      this.employeeTimeOffFieldError.clearErrors();
      this.employeeWorkScheduleFieldError.clearErrors();

      this.clearErrorTab();
    },

    async saveCurrentForm() {
      this.clearAllErrors();
      try {
        this.loading = true;

        if (!this.validateForm('employeeFormDetail')) {
          return this.setErrorTab(EmployeeProfileTabs.Detail);
        }
        if (!this.validateForm('employeeFormTimeoff')) {
          return this.setErrorTab(EmployeeProfileTabs.Timeoff);
        }
        if (!this.validateForm('employeeFormSchedule')) {
          return this.setErrorTab(EmployeeProfileTabs.Schedule);
        }
        await this.saveEmployee();
        await this.saveEmployeeTimeOff();
        await this.saveEmployeeWorkSchedule();
        this.close();
      } catch (err) {
        const error = err as AxiosError;
        // handle error message thrown down from function
        if (error.response) {
          if (error.response.data.detail) {
            this.errorMessageDetail = error.response.data.detail;
          }
          this.errorMessage = ErrorManager.extractApiError(error);
        }
      } finally {
        this.loading = false;
      }
    },

    async saveEmployee() {
      const employeeDetailFormCard = this.$refs.employeeDetailFormCard;

      if (employeeDetailFormCard) {
        try {
          await employeeDetailFormCard.saveEmployee();
        } catch (err) {
          const error = err as AxiosError;
          if (error.response) {
            if (EmployeeFieldError.isEmployeeFieldError(error.response.data)) {
              this.employeeDetailFieldError = new EmployeeFieldError(
                error.response.data
              );
            }
          }

          this.setErrorTab(EmployeeProfileTabs.Detail);

          // throw into default catch to not repeat code in saveCurrentForm method
          throw error;
        }
      }
    },

    async saveEmployeeTimeOff() {
      const employeeTimeOffDetailFormCard = this.$refs
        .employeeTimeOffDetailFormCard;

      if (employeeTimeOffDetailFormCard) {
        try {
          await employeeTimeOffDetailFormCard.saveEmployeeTimeOff();
        } catch (err) {
          const error = err as AxiosError;
          if (error.response) {
            if (
              Array.isArray(error.response.data) &&
              EmployeeTimeOffFieldError.isEmployeeTimeOffFieldError({
                entitlements: error.response.data
              })
            ) {
              this.employeeTimeOffFieldError = new EmployeeTimeOffFieldError({
                entitlements: error.response.data
              });
            } else if (
              EmployeeTimeOffFieldError.isEmployeeTimeOffFieldError(
                error.response.data
              )
            ) {
              this.employeeTimeOffFieldError = new EmployeeTimeOffFieldError(
                error.response.data
              );
            }
          }

          this.setErrorTab(EmployeeProfileTabs.Timeoff);

          // throw into default catch to not repeat code in saveCurrentForm
          throw error;
        }
      }
    },

    async saveEmployeeWorkSchedule() {
      const employeeScheduleDetailFormCard = this.$refs
        .employeeScheduleDetailFormCard;

      if (employeeScheduleDetailFormCard) {
        try {
          await employeeScheduleDetailFormCard.saveEmployeeWorkSchedule();
        } catch (err) {
          const error = err as AxiosError;
          if (error.response) {
            if (
              EmployeeWorkScheduleFieldError.isEmployeeWorkScheduleFieldError(
                error.response.data
              )
            ) {
              this.employeeWorkScheduleFieldError = new EmployeeWorkScheduleFieldError(
                error.response.data
              );
            }
          }

          this.setErrorTab(EmployeeProfileTabs.Schedule);

          // throw into default catch to not repeat code in saveCurrentForm
          throw error;
        }
      }
    }
  }
});
